<script setup>
import { onBeforeMount, onMounted, ref, provide } from "vue";
import { useEnvStore, useAuthStore } from "@dutypay/front-end-design-system/src/stores";
import { interceptor } from "@dutypay/front-end-design-system/src/utilities";
import { get } from "lodash";
import qs from "qs";
import { to } from "await-to-js";
import { getPersistedLocale } from "./utils/jurisdiction";
import { buildRedirectUrl } from "./utils/redirect";

const envStore = useEnvStore();
const authStore = useAuthStore();
const hasLogoutNotification = ref(false);

function hideLogoutNotification() {
  hasLogoutNotification.value = false;
}
provide("logoutNotification", {
  hideLogoutNotification,
  hasLogoutNotification,
});

const accountActivationNotification = ref({
  success: false,
  error: false,
});
provide("accountActivationNotification", { accountActivationNotification });

async function initSSO() {
  envStore.setGlobalLoading();

  let urlParams = qs.parse(window.location.search.replace("?", ""));
  if (Object.prototype.hasOwnProperty.call(urlParams, "logout")) {
    let err, logout;
    [err, logout] = await to(authStore.logout());
    if (err && envStore.debug) {
      console.log(err);
    }
    envStore.releaseGlobalLoading();
    if (get(logout, "status") === 200) {
      hasLogoutNotification.value = true;
    }
  }

  let err, ottConfig;
  if (urlParams && urlParams.session) {
    [err, ottConfig] = await to(
      interceptor({
        url: `${process.env.VUE_APP_API_DUTYPAY}/api/v0/authenticate/legacy`,
        data: { session: urlParams.session },
        method: "POST",
      })
    );
    if (err) {
      envStore.releaseGlobalLoading();
      let redirect = "";
      if (urlParams.redirect) {
        redirect += "/?redirect=" + urlParams.redirect;
      }
      window.history.pushState({}, document.title, redirect);
      return;
    }
  } else {
    [err, ottConfig] = await to(
      interceptor({
        url: `${process.env.VUE_APP_API_DUTYPAY}/api/v0/authenticate/sso`,
        headers: { Authorization: "Bearer " + localStorage.getItem("accessToken") },
        method: "POST",
      })
    );
    if (err) {
      await to(authStore.logout());
      envStore.releaseGlobalLoading();
      return false;
    }
  }
  let i18n = getPersistedLocale(process.env.VUE_APP_I18N_LOCALE);
  let url = qs.parse(window.location.search.substring(1));
  if (url && url.redirect) {
    window.location.href = buildRedirectUrl(url.redirect, {
      i18n,
      token: ottConfig.data.result.ott,
    });
    return;
  }
  // TODO: Add host URL to .env file
  window.location.href = `https://${
    envStore.isDevelopment ? "dev-" : ""
  }app.contractshub.eu/#i18n=${i18n}&token=${ottConfig.data.result.ott}`;
  return true;
}

onBeforeMount(async () => {
  try {
    const match = window.location.pathname.match(/^\/register\/confirm\/([0-9a-fA-F-]{36})$/);
    if (match && match[1]) {
      // If the current path matches /register/confirm/:hubsystemRequestId
      await requestAccountActivation(match[1]);
      return;
    }
  } catch (e) {
    if (envStore.debug) console.error("register confirm detection error", e);
  }
    
    await initSSO();

});

async function requestAccountActivation(id) {
  envStore.setGlobalLoading();
  try {
    const [response, err] = await to(
      interceptor({
        url: `${process.env.VUE_APP_API_DUTYPAY}/api/v0/requests/public/${id}/start`,
        data: JSON.stringify({}),
        method: "POST",
      })
    );

    if (err) {
      if (envStore.debug) console.error("Register confirmation API error:", err);
      return;
    }

    if (response.status !== 200) {
      accountActivationNotification.value.error = true;
      if (envStore.debug)
        console.error("Register confirmation failed with status:", response.status);
      return;
    } else {
      accountActivationNotification.value.success = true;
    }
  } finally {
    envStore.releaseGlobalLoading();
  }
}

onMounted(async () => {
  await envStore.fetchHosts();
});
</script>

<style lang="scss">
@use "@dutypay/front-end-design-system/src/scss" as design-system;
@include design-system.use-application-color("dutypay");
@include design-system.use-theme("light");

$--color-primary: #515151;

body {
  background-color: #2d2d2d !important; /* Old browsers */
  background: -moz-linear-gradient(130deg, #2d2d2d 1%, #404040 100%); /* FF3.6-15 */
  background: -webkit-linear-gradient(
    130deg,
    #2d2d2d 1%,
    #404040 100%
  ); /* Chrome10-25,Safari5.1-6 */
  background: linear-gradient(
    130deg,
    #2d2d2d 1%,
    #404040 100%
  ); /* W3C, IE10+, FF16+, Chrome26+, Opera12+, Safari7+ */
  filter: progid:DXImageTransform.Microsoft.gradient(startColorstr='#2d2d2d', endColorstr='#404040', GradientType=1); /* IE6-9 fallback on horizontal gradient */
}

#app {
  color: white;
  background-color: unset !important;
}
</style>

<i18n>
{
  "ar-AE": {
    "notification": {
      "logout": {
        "success": {
          "title": "عمل ناجح"
        }
      }
    }
  },
  "ar-DZ": {
    "notification": {
      "logout": {
        "success": {
          "title": "عمل ناجح"
        }
      }
    }
  },
  "ar-EG": {
    "notification": {
      "logout": {
        "success": {
          "title": "عمل ناجح"
        }
      }
    }
  },
  "ar-LB": {
    "notification": {
      "logout": {
        "success": {
          "title": "عمل ناجح"
        }
      }
    }
  },
  "ar-MA": {
    "notification": {
      "logout": {
        "success": {
          "title": "عمل ناجح"
        }
      }
    }
  },
  "ar-SA": {
    "notification": {
      "logout": {
        "success": {
          "title": "عمل ناجح"
        }
      }
    }
  },
  "ar-SY": {
    "notification": {
      "logout": {
        "success": {
          "title": "عمل ناجح"
        }
      }
    }
  },
  "ar-TN": {
    "notification": {
      "logout": {
        "success": {
          "title": "عمل ناجح"
        }
      }
    }
  },
  "bg-BG": {
    "notification": {
      "logout": {
        "success": {
          "title": "Успешно действие"
        }
      }
    }
  },
  "bs-BA": {
    "notification": {
      "logout": {
        "success": {
          "title": "Uspješno obavljeno"
        }
      }
    }
  },
  "cs-CZ": {
    "notification": {
      "logout": {
        "success": {
          "title": "Úspěšná akce"
        }
      }
    }
  },
  "da-DK": {
    "notification": {
      "logout": {
        "success": {
          "title": "Vellykket handling"
        }
      }
    }
  },
  "de-DE": {
    "notification": {
      "logout": {
        "success": {
          "title": "Erfolgreiche Aktion"
        }
      }
    }
  },
  "de-AT": {
    "notification": {
      "logout": {
        "success": {
          "title": "Erfolgreiche Aktion"
        }
      }
    }
  },
  "de-CH": {
    "notification": {
      "logout": {
        "success": {
          "title": "Erfolgreiche Aktion"
        }
      }
    }
  },
  "de-LU": {
    "notification": {
      "logout": {
        "success": {
          "title": "Erfolgreiche Aktion"
        }
      }
    }
  },
  "el-GR": {
    "notification": {
      "logout": {
        "success": {
          "title": "Επιτυχημένη ενέργεια"
        }
      }
    }
  },
  "el-CY": {
    "notification": {
      "logout": {
        "success": {
          "title": "Επιτυχημένη ενέργεια"
        }
      }
    }
  },
  "en-GB": {
    "notification": {
      "logout": {
        "success": {
          "title": "Successful Action"
        }
      }
    }
  },
  "en-IE": {
    "notification": {
      "logout": {
        "success": {
          "title": "Successful Action"
        }
      }
    }
  },
  "en-US": {
    "notification": {
      "logout": {
        "success": {
          "title": "Successful Action"
        }
      }
    }
  },
  "es-ES": {
    "notification": {
      "logout": {
        "success": {
          "title": "Listo"
        }
      }
    }
  },
  "et-EE": {
    "notification": {
      "logout": {
        "success": {
          "title": "Edukas tegevus"
        }
      }
    }
  },
  "fi-FI": {
    "notification": {
      "logout": {
        "success": {
          "title": "Onnistunut toiminta"
        }
      }
    }
  },
  "fr-BE": {
    "notification": {
      "logout": {
        "success": {
          "title": "Action réussie"
        }
      }
    }
  },
  "fr-CH": {
    "notification": {
      "logout": {
        "success": {
          "title": "Action réussie"
        }
      }
    }
  },
  "fr-FR": {
    "notification": {
      "logout": {
        "success": {
          "title": "Action réussie"
        }
      }
    }
  },
  "fr-LU": {
    "notification": {
      "logout": {
        "success": {
          "title": "Action réussie"
        }
      }
    }
  },
  "ga-IE": {
    "notification": {
      "logout": {
        "success": {
          "title": "Gníomh rathúil"
        }
      }
    }
  },
  "he-IL": {
    "notification": {
      "logout": {
        "success": {
          "title": "פעולה מוצלחת"
        }
      }
    }
  },
  "hi-IN": {
    "notification": {
      "logout": {
        "success": {
          "title": "सफल कार्रवाई"
        }
      }
    }
  },
  "hr-HR": {
    "notification": {
      "logout": {
        "success": {
          "title": "Uspješna akcija"
        }
      }
    }
  },
  "hu-HU": {
    "notification": {
      "logout": {
        "success": {
          "title": "Sikeres művelet"
        }
      }
    }
  },
  "it-CH": {
    "notification": {
      "logout": {
        "success": {
          "title": "Azione di successo"
        }
      }
    }
  },
  "it-IT": {
    "notification": {
      "logout": {
        "success": {
          "title": "Azione riuscita"
        }
      }
    }
  },
  "ja-JP": {
    "notification": {
      "logout": {
        "success": {
          "title": "成功したアクション"
        }
      }
    }
  },
  "ko-KR": {
    "notification": {
      "logout": {
        "success": {
          "title": "성공적인 액션"
        }
      }
    }
  },
  "lt-LT": {
    "notification": {
      "logout": {
        "success": {
          "title": "Sėkmingas veiksmas"
        }
      }
    }
  },
  "lv-LV": {
    "notification": {
      "logout": {
        "success": {
          "title": "Veiksmīga darbība"
        }
      }
    }
  },
  "mt-MT": {
    "notification": {
      "logout": {
        "success": {
          "title": "Azzjoni ta' suċċess"
        }
      }
    }
  },
  "nb-NO": {
    "notification": {
      "logout": {
        "success": {
          "title": "Vellykket handling"
        }
      }
    }
  },
  "nl-BE": {
    "notification": {
      "logout": {
        "success": {
          "title": "Geslaagde actie"
        }
      }
    }
  },
  "nl-NL": {
    "notification": {
      "logout": {
        "success": {
          "title": "Geslaagde actie"
        }
      }
    }
  },
  "pl-PL": {
    "notification": {
      "logout": {
        "success": {
          "title": "Czynność zakończona powodzeniem"
        }
      }
    }
  },
  "pt-PT": {
    "notification": {
      "logout": {
        "success": {
          "title": "Ação bem-sucedida"
        }
      }
    }
  },
  "ro-RO": {
    "notification": {
      "logout": {
        "success": {
          "title": "Acțiune reușită"
        }
      }
    }
  },
  "ru-RU": {
    "notification": {
      "logout": {
        "success": {
          "title": "Успешное действие"
        }
      }
    }
  },
  "sk-SK": {
    "notification": {
      "logout": {
        "success": {
          "title": "Úspešná akcia"
        }
      }
    }
  },
  "sl-SI": {
    "notification": {
      "logout": {
        "success": {
          "title": "Uspešno opravljeno"
        }
      }
    }
  },
  "sv-SE": {
    "notification": {
      "logout": {
        "success": {
          "title": "Åtgärd utförd"
        }
      }
    }
  },
  "th-TH": {
    "notification": {
      "logout": {
        "success": {
          "title": "การดำเนินการที่ประสบความสำเร็จ"
        }
      }
    }
  },
  "tr-CY": {
    "notification": {
      "logout": {
        "success": {
          "title": "Başarılı eylem"
        }
      }
    }
  },
  "tr-TR": {
    "notification": {
      "logout": {
        "success": {
          "title": "Başarılı eylem"
        }
      }
    }
  },
  "uk-UA": {
    "notification": {
      "logout": {
        "success": {
          "title": "Успішна дія"
        }
      }
    }
  },
  "vi-VN": {
    "notification": {
      "logout": {
        "success": {
          "title": "Hành động thành công"
        }
      }
    }
  },
  "zh-CN": {
    "notification": {
      "logout": {
        "success": {
          "title": "操作成功"
        }
      }
    }
  }
}
</i18n>

<template>
  <div id="app">
    <router-view />
  </div>
</template>
